import React, { useCallback, useMemo, useState } from 'react';
import { ArrowDownIcon, ArrowUpIcon, Box, capitalizeFirstLetter, List } from '@common-fe/common-fe';
import dayjs from 'dayjs';

import { DEFAULT_DATE_TIME_FORMAT_SHORT, EMPTY_FIELD_VALUE } from '@/common/constants';

import { EventTypeLabel, FailureReasonTypeLabel, LoginHistory, LoginStatusType, LoginStatusTypeLabel, TRIGGER_ONLY_EVENTS } from './loginHistory.types';
import LoginHistoryListItemDetails from './LoginHistoryListItemDetails';

const LoginHistoryListItem: React.FC<Partial<LoginHistory>> = ({
  createdAt,
  username,
  eventType,
  statusType,
  failureReasonType,
  personalEmail,
  employeeIds,
  contactId,
  ipAddress,
  geolocation,
  userAgent,
  sourceType,
  mfaStatusType,
}) => {
  const [isExpanded, setIsExpanded] = useState(false);

  const handleToggle = useCallback(() => {
    setIsExpanded(!isExpanded);
  }, [setIsExpanded, isExpanded]);

  const isTriggerOnly = useMemo(() => eventType ? TRIGGER_ONLY_EVENTS.includes(eventType) : false, [eventType]);
  const createdAtTitle = useMemo(() => createdAt ? dayjs(createdAt).format(DEFAULT_DATE_TIME_FORMAT_SHORT) : EMPTY_FIELD_VALUE, [createdAt]);
  const usernameTitle = useMemo(() => username || EMPTY_FIELD_VALUE, [username]);
  const eventTypeTitle = useMemo(() => eventType ? EventTypeLabel?.[eventType] : EMPTY_FIELD_VALUE, [eventType]);
  const statusTypeTitle = useMemo(() => isTriggerOnly ? '' : (statusType ? capitalizeFirstLetter(LoginStatusTypeLabel?.[statusType]) : EMPTY_FIELD_VALUE), [isTriggerOnly, statusType]);
  const failureReasonTypeTitle = useMemo(() => isTriggerOnly ? '' : (failureReasonType ? FailureReasonTypeLabel?.[failureReasonType] : ''), [isTriggerOnly, failureReasonType]);

  return (
    <List.Item testId="login-history-list_item">
      <Box width="100%" direction="column">
        <Box width="100%" direction="row" align="center" height={{ min: '40px' }}>
          <Box
            data-testId="login-history-list_toggle-item-details"
            onClick={handleToggle}
            margin={{ right: 'spacing16' }}
          >
            {isExpanded ? <ArrowUpIcon /> : <ArrowDownIcon />}
          </Box>

          <List.Item.Text
            style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}
            title={createdAtTitle}
            weight={400}
          >
            {createdAtTitle}
          </List.Item.Text>

          <List.Item.Text
            style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}
            title={usernameTitle}
            weight={700}
          >
            {usernameTitle}
          </List.Item.Text>

          <List.Item.Text
            style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}
            title={eventTypeTitle}
            weight={400}
          >
            {eventTypeTitle}
          </List.Item.Text>

          <List.Item.Text
            style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}
            title={statusTypeTitle}
            weight={400}
            color={statusType === LoginStatusType.SUCCESS ? 'success' : 'danger'}
          >
            {statusTypeTitle}
          </List.Item.Text>

          <List.Item.Text
            style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}
            title={failureReasonTypeTitle}
            color="danger"
            weight={400}
          >
            {failureReasonTypeTitle}
          </List.Item.Text>
        </Box>

        {isExpanded && (
          <LoginHistoryListItemDetails
            contactId={contactId}
            employeeIds={employeeIds}
            ipAddress={ipAddress}
            geolocation={geolocation}
            userAgent={userAgent}
            personalEmail={personalEmail}
            eventType={eventType}
            sourceType={sourceType}
            mfaStatusType={mfaStatusType}
          />
        )}
      </Box>
    </List.Item>
  );
};

export default LoginHistoryListItem;


